import { X } from 'lucide-react'
import s from './GridFilterPills.module.css'

export interface GridFilterPill {
  /** The column key — `grid-filter-<key>`. */
  key: string
  /** The column's label: "Status". */
  column: string
  /** The condition in words: "is", "is any of", "contains". */
  condition: string
  /** The value in words: "Active" or "Service, Subscription". */
  value: string
  onRemove: () => void
  /** Opens that column's filter editor at the pill (the same one as ⋮ → Filter…). */
  onOpen?: (anchor: HTMLElement) => void
}

export interface GridFilterPillsProps {
  pills: readonly GridFilterPill[]
  onClearAll?: () => void
  testId?: string
}

/**
 * THE APPLIED FILTERS (#2375) — a row directly under the bar that exists ONLY
 * while at least one filter is applied. With none it renders nothing: no empty
 * band, no "No filters" text (marble-uiux non-negotiable 7: nothing takes room
 * it does not use).
 *
 * `Column · condition · value ✕`, the value in the heavier weight, and a
 * brand-text "Clear all" at the end. The pill's body reopens the column's
 * editor; its ✕ removes that filter alone.
 */
export function GridFilterPills({ pills, onClearAll, testId = 'grid-filters' }: GridFilterPillsProps) {
  if (pills.length === 0) return null
  return (
    <div className={s.row} data-testid={testId} role="group" aria-label="Applied filters">
      {pills.map((pill) => {
        const words = (
          <>
            <span>{pill.column}</span>
            <span className={s.sep} aria-hidden="true">·</span>
            <span>{pill.condition}</span>
            <span className={s.sep} aria-hidden="true">·</span>
            <strong className={s.value}>{pill.value}</strong>
          </>
        )
        return (
          <span key={pill.key} className={s.pill} data-testid={`grid-filter-${pill.key}`}>
            {pill.onOpen ? (
              <button type="button" className={s.body}
                aria-label={`Edit filter: ${pill.column} ${pill.condition} ${pill.value}`}
                onClick={(event) => pill.onOpen!(event.currentTarget)}>{words}</button>
            ) : <span className={s.body}>{words}</span>}
            <button type="button" className={s.remove} aria-label={`Remove filter ${pill.column}`}
              onClick={pill.onRemove}>
              <X size={12} strokeWidth={2.5} aria-hidden="true" />
            </button>
          </span>
        )
      })}
      {onClearAll && (
        <button type="button" className={s.clearAll} data-testid={`${testId}-clear`} onClick={onClearAll}>
          Clear all
        </button>
      )}
    </div>
  )
}
